import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import {
  Star, Cube, User, Truck, Plus, X, Eye, EyeSlash, Trash,
  ChartBar, TrendUp, TrendDown, Package, ArrowUpRight, FunnelSimple
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'product', label: 'Product Reviews', icon: Cube, color: '#7d64ff' },
  { key: 'seller', label: 'Seller Reviews', icon: User, color: '#28cd8c' },
  { key: 'logistics', label: 'Logistics Reviews', icon: Truck, color: '#f6a609' },
];

/* ── Stars ─── */
function Stars({ rating, size = 14, interactive, onChange }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" disabled={!interactive} onClick={() => interactive && onChange?.(i)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}>
          <Star size={size} weight={i <= rating ? 'fill' : 'regular'}
            className={i <= rating ? 'text-amber-400' : 'text-gray-300'} />
        </button>
      ))}
    </div>
  );
}

/* ── Stats Card ─── */
function StatsOverview({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      {TABS.map(tab => {
        const s = stats[tab.key] || {};
        return (
          <div key={tab.key} className="bg-white border border-[#e5e5ea] rounded-xl p-4 flex items-center gap-4" data-testid={`stats-${tab.key}`}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tab.color + '12' }}>
              <tab.icon size={22} style={{ color: tab.color }} weight="duotone" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-extrabold text-[#0A0A0A]">{s.avg_rating || 0}</span>
                <Stars rating={Math.round(s.avg_rating || 0)} size={12} />
              </div>
              <p className="text-[12px] text-[#9c9db7]">{s.total || 0} {tab.label.toLowerCase()}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Rating Distribution Bar ─── */
function RatingDistribution({ stats }) {
  if (!stats || !stats.total) return null;
  const bars = [
    { stars: 5, count: stats.stars_5 || 0 },
    { stars: 4, count: stats.stars_4 || 0 },
    { stars: 3, count: stats.stars_3 || 0 },
    { stars: 2, count: stats.stars_2 || 0 },
    { stars: 1, count: stats.stars_1 || 0 },
  ];
  const max = Math.max(...bars.map(b => b.count), 1);

  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4 mb-4">
      <h4 className="text-sm font-bold text-[#1d1d1f] mb-3">Rating Distribution</h4>
      <div className="space-y-1.5">
        {bars.map(b => (
          <div key={b.stars} className="flex items-center gap-2">
            <span className="text-[11px] text-[#9c9db7] w-4 text-right">{b.stars}</span>
            <Star size={10} weight="fill" className="text-amber-400" />
            <div className="flex-1 h-2 bg-[#f5f5f7] rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${(b.count / max) * 100}%` }} />
            </div>
            <span className="text-[11px] text-[#5a5c87] w-6 text-right">{b.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Top/Worst Rated Products ─── */
function RatedProducts({ title, products, icon, color }) {
  if (!products?.length) return null;
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h4 className="text-sm font-bold text-[#1d1d1f]">{title}</h4>
      </div>
      <div className="space-y-2">
        {products.map((p, i) => (
          <div key={i} className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-[#fafafe]">
            <span className="text-[13px] text-[#1d1d1f] truncate flex-1 mr-2">{p.name}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Stars rating={Math.round(p.avg_rating)} size={11} />
              <span className="text-[12px] font-semibold" style={{ color }}>{p.avg_rating}</span>
              <span className="text-[10px] text-[#9c9db7]">({p.count})</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Add Review Dialog ─── */
function AddReviewDialog({ open, onClose, onSubmit, reviewType, products, sellers, orders }) {
  const [form, setForm] = useState({ entity_id: '', rating: 0, title: '', comment: '', logistics_partner: '', delivery_rating: 0, packaging_rating: 0, condition_rating: 0 });

  if (!open) return null;

  const entities = reviewType === 'product' ? products : reviewType === 'seller' ? sellers : orders;
  const entityLabel = reviewType === 'product' ? 'Product' : reviewType === 'seller' ? 'Seller' : 'Order';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.entity_id || !form.rating) { toast.error('Please select an entity and rating'); return; }
    onSubmit({ ...form, review_type: reviewType });
    setForm({ entity_id: '', rating: 0, title: '', comment: '', logistics_partner: '', delivery_rating: 0, packaging_rating: 0, condition_rating: 0 });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e5ea]">
          <h3 className="font-bold text-[#1d1d1f]">Add {entityLabel} Review</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[#f5f5f7]"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-[#5a5c87] uppercase tracking-wider mb-1 block">{entityLabel}</label>
            <select value={form.entity_id} onChange={e => setForm(f => ({ ...f, entity_id: e.target.value }))}
              className="w-full px-3 py-2 text-[13px] border border-[#e5e5ea] rounded-xl focus:outline-none focus:border-[#7d64ff]" data-testid="review-entity-select">
              <option value="">Select {entityLabel}...</option>
              {entities?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-[#5a5c87] uppercase tracking-wider mb-1 block">Overall Rating</label>
            <Stars rating={form.rating} size={24} interactive onChange={r => setForm(f => ({ ...f, rating: r }))} />
          </div>
          {reviewType === 'logistics' && (
            <>
              <div>
                <label className="text-[12px] font-semibold text-[#5a5c87] uppercase tracking-wider mb-1 block">Logistics Partner</label>
                <select value={form.logistics_partner} onChange={e => setForm(f => ({ ...f, logistics_partner: e.target.value }))}
                  className="w-full px-3 py-2 text-[13px] border border-[#e5e5ea] rounded-xl focus:outline-none focus:border-[#7d64ff]">
                  <option value="">Select partner...</option>
                  {['BlueDart', 'Delhivery', 'Ecom Express', 'DTDC', 'Shadowfax'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[['delivery_rating', 'Delivery'], ['packaging_rating', 'Packaging'], ['condition_rating', 'Condition']].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-[10px] font-semibold text-[#9c9db7] uppercase mb-0.5 block">{label}</label>
                    <Stars rating={form[key]} size={14} interactive onChange={r => setForm(f => ({ ...f, [key]: r }))} />
                  </div>
                ))}
              </div>
            </>
          )}
          <div>
            <label className="text-[12px] font-semibold text-[#5a5c87] uppercase tracking-wider mb-1 block">Title (optional)</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-2 text-[13px] border border-[#e5e5ea] rounded-xl focus:outline-none focus:border-[#7d64ff]" placeholder="Brief summary..." data-testid="review-title-input" />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-[#5a5c87] uppercase tracking-wider mb-1 block">Comment</label>
            <textarea value={form.comment} onChange={e => setForm(f => ({ ...f, comment: e.target.value }))} rows={3}
              className="w-full px-3 py-2 text-[13px] border border-[#e5e5ea] rounded-xl focus:outline-none focus:border-[#7d64ff] resize-none" placeholder="Share your experience..." data-testid="review-comment-input" />
          </div>
          <Button type="submit" className="w-full bg-[#7d64ff] hover:bg-[#6a54e0] text-white" data-testid="submit-review-btn">Submit Review</Button>
        </form>
      </div>
    </div>
  );
}

/* ── Review Card ─── */
function ReviewCard({ review, isAdmin, onModerate, onDelete }) {
  const isLogistics = review.review_type === 'logistics';
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`review-${review.review_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Stars rating={review.rating} size={13} />
            <span className="text-[12px] font-semibold text-[#1d1d1f]">{review.rating}/5</span>
            {review.title && <span className="text-[13px] font-medium text-[#1d1d1f]">&mdash; {review.title}</span>}
          </div>
          <p className="text-[12px] text-[#9c9db7] mt-0.5">
            {review.entity_name} &middot; by <span className="font-medium text-[#5a5c87]">{review.reviewer_name}</span>
            {review.reviewer_role === 'admin' && <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-[#f2f0ff] text-[#7d64ff] rounded font-semibold">Admin</span>}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${review.status === 'published' ? 'bg-emerald-50 text-emerald-600' : review.status === 'flagged' ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'}`}>
            {review.status}
          </span>
          {isAdmin && (
            <>
              <button onClick={() => onModerate(review.review_id, review.status === 'published' ? 'hidden' : 'published')}
                className="p-1 rounded hover:bg-[#f5f5f7] text-[#9c9db7]" title={review.status === 'published' ? 'Hide' : 'Publish'}>
                {review.status === 'published' ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => onDelete(review.review_id)} className="p-1 rounded hover:bg-red-50 text-[#9c9db7] hover:text-red-500">
                <Trash size={14} />
              </button>
            </>
          )}
        </div>
      </div>
      {review.comment && <p className="text-[13px] text-[#3c3c64] leading-relaxed mb-2">{review.comment}</p>}
      {isLogistics && (
        <div className="flex flex-wrap gap-3 text-[11px] text-[#5a5c87] mt-1 pt-2 border-t border-[#f0f0f0]">
          {review.logistics_partner && <span className="px-2 py-0.5 bg-[#f5f5f7] rounded-full font-medium">{review.logistics_partner}</span>}
          {review.delivery_rating > 0 && <span>Delivery: <Stars rating={review.delivery_rating} size={10} /></span>}
          {review.packaging_rating > 0 && <span>Packaging: <Stars rating={review.packaging_rating} size={10} /></span>}
          {review.condition_rating > 0 && <span>Condition: <Stars rating={review.condition_rating} size={10} /></span>}
        </div>
      )}
      <p className="text-[10px] text-[#c7c7cc] mt-2">{new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════ */
export default function ReviewsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('product');
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [entityStats, setEntityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // Entity lists for the add form
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [orders, setOrders] = useState([]);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ review_type: activeTab, page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/reviews?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/reviews/stats`, { withCredentials: true })
      ]);
      setReviews(reviewsRes.data.reviews);
      setTotal(reviewsRes.data.total);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activeTab, page, statusFilter]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Load entity lists for add form
  useEffect(() => {
    const loadEntities = async () => {
      try {
        const [prodRes, orderRes] = await Promise.all([
          axios.get(`${API}/api/products?limit=100`, { withCredentials: true }),
          axios.get(`${API}/api/orders?limit=100`, { withCredentials: true })
        ]);
        setProducts((prodRes.data.products || []).map(p => ({ id: p.product_id, name: p.name })));
        setOrders((orderRes.data.orders || []).map(o => ({ id: o.order_id, name: `${o.marketplace_order_id} - ${o.customer_name}` })));
        if (isAdmin) {
          const sellerRes = await axios.get(`${API}/api/admin/sellers`, { withCredentials: true });
          const sellerList = sellerRes.data.sellers || sellerRes.data || [];
          setSellers(sellerList.map(s => ({ id: s.user_id, name: s.name || s.email })));
        }
      } catch {}
    };
    loadEntities();
  }, [isAdmin]);

  const handleSubmitReview = async (formData) => {
    try {
      await axios.post(`${API}/api/reviews`, formData, { withCredentials: true });
      toast.success('Review submitted');
      fetchReviews();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  const handleModerate = async (reviewId, newStatus) => {
    try {
      await axios.patch(`${API}/api/reviews/${reviewId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Review ${newStatus}`);
      fetchReviews();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (reviewId) => {
    try {
      await axios.delete(`${API}/api/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted');
      fetchReviews();
    } catch { toast.error('Failed to delete'); }
  };

  const tabConfig = TABS.find(t => t.key === activeTab);
  const totalPages = Math.ceil(total / 20);

  return (
    <DashboardLayout
      title="Ratings & Reviews"
      subtitle="Monitor product, seller, and logistics reviews"
      actions={
        <Button onClick={() => setShowAdd(true)} className="bg-[#7d64ff] hover:bg-[#6a54e0] text-white text-sm" data-testid="add-review-btn">
          <Plus size={16} className="mr-1.5" /> Add Review
        </Button>
      }
    >
      {/* Stats overview */}
      <StatsOverview stats={stats} />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-[#e5e5ea] overflow-x-auto" data-testid="review-tabs">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPage(1); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.key ? 'border-[#7d64ff] text-[#7d64ff]' : 'border-transparent text-[#9c9db7] hover:text-[#5a5c87]'}`}
            data-testid={`tab-${tab.key}`}>
            <tab.icon size={16} />
            {tab.label}
            {stats?.[tab.key]?.total > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#f5f5f7]">{stats[tab.key].total}</span>
            )}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Main content - review list */}
        <div className="lg:col-span-3">
          {/* Filter bar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[12px] text-[#9c9db7]">
              <FunnelSimple size={14} />
              <span>{total} reviews</span>
            </div>
            {isAdmin && (
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                className="text-[12px] px-2.5 py-1.5 border border-[#e5e5ea] rounded-lg bg-white" data-testid="status-filter">
                <option value="">All Status</option>
                <option value="published">Published</option>
                <option value="hidden">Hidden</option>
                <option value="flagged">Flagged</option>
              </select>
            )}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4].map(i => <div key={i} className="h-24 skeleton rounded-xl" />)}
            </div>
          ) : reviews.length === 0 ? (
            <div className="bg-white border border-[#e5e5ea] rounded-xl p-8 text-center">
              <Star size={40} className="text-[#d1d1d6] mx-auto mb-3" weight="duotone" />
              <h4 className="font-bold text-[#1d1d1f] mb-1">No reviews yet</h4>
              <p className="text-[13px] text-[#9c9db7]">Reviews will appear here once submitted</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {reviews.map(r => (
                  <ReviewCard key={r.review_id} review={r} isAdmin={isAdmin} onModerate={handleModerate} onDelete={handleDelete} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <span className="text-[12px] text-[#9c9db7]">Page {page} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar - insights */}
        <div className="space-y-4">
          {stats?.top_rated_products?.length > 0 && (
            <RatedProducts title="Top Rated Products" products={stats.top_rated_products}
              icon={<TrendUp size={14} className="text-emerald-500" weight="bold" />} color="#28cd8c" />
          )}
          {stats?.worst_rated_products?.length > 0 && (
            <RatedProducts title="Needs Improvement" products={stats.worst_rated_products}
              icon={<TrendDown size={14} className="text-red-500" weight="bold" />} color="#ea4b5a" />
          )}
        </div>
      </div>

      <AddReviewDialog open={showAdd} onClose={() => setShowAdd(false)} onSubmit={handleSubmitReview}
        reviewType={activeTab} products={products} sellers={sellers} orders={orders} />
    </DashboardLayout>
  );
}
